import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { cn } from '../lib/utils';
import { emeraldBg, blueBg, amberBg, GRAY_BG } from '../lib/heatmapColors';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Generic row-by-month heatmap table.
 *
 * @param {Object[]} rows        - sorted array of row objects (each must have `id` and `name`)
 * @param {string}   rowHeader   - column header for the row label (default "Name")
 * @param {Function} rowLabel    - (row) => display string (default row.name)
 * @param {Function} valueAccessor - (row, monthIndex) => number | null
 * @param {string}   colorScale  - "emerald" | "blue" | "amber"
 * @param {Function} formatter   - (value) => display string
 * @param {string}   emptyLabel  - label for null cells (default "\u2013")
 * @param {string}   title       - card title
 * @param {string}   [subtitle]  - optional subtitle below the table
 * @param {Function} [isEmptyCell] - (row, monthIndex, value) => boolean, override empty detection
 * @param {Function} [monthTotalAccessor] - (monthIndex) => number | null
 * @param {Function} [monthTotalFormatter] - (value) => string
 * @param {Function} [ytdAccessor] - (row) => number | null
 * @param {string}   [ytdLabel]  - header for YTD column (default "YTD")
 * @param {Function} [ytdFormatter] - (value) => string, defaults to formatter
 * @param {Function} [ytdCellRenderer] - (value, row) => React node, custom renderer for YTD cells
 * @param {number|null} [ytdTotal] - YTD value for aggregate Total row
 * @param {React.ReactNode} [ytdTotalRenderer] - custom renderer for the Total row YTD cell
 * @param {number|null} [forecastMonthStart] - 0-based month index where forecast begins (cells in [start, end] get forecast styling in the total row)
 * @param {number|null} [forecastMonthEnd] - 0-based month index where forecast ends (inclusive); defaults to 11 if forecastMonthStart is set
 */
export function MonthlyHeatmap({
  rows,
  rowHeader = 'Name',
  rowLabel,
  valueAccessor,
  colorScale = 'blue',
  formatter,
  emptyLabel = '\u2013',
  title,
  subtitle,
  isEmptyCell,
  monthTotalAccessor,
  monthTotalFormatter,
  ytdAccessor,
  ytdLabel = 'YTD',
  ytdFormatter,
  ytdCellRenderer,
  ytdTotal,
  ytdTotalRenderer,
  forecastMonthStart,
  forecastMonthEnd,
}) {
  const getLabel = rowLabel || ((row) => row.name);

  // Compute scale reference across all non-null monthly values
  const scaleRef = useMemo(() => {
    const vals = [];
    rows.forEach(r => {
      for (let mi = 0; mi < 12; mi++) {
        const v = valueAccessor(r, mi);
        if (v != null && v !== 0) vals.push(v);
      }
    });
    if (vals.length === 0) return 0;
    if (colorScale === 'amber') {
      return Math.max(...vals.map(Math.abs));
    }
    const positives = vals.filter(v => v > 0);
    positives.sort((a, b) => a - b);
    return positives.length > 0 ? positives[positives.length - 1] : 0;
  }, [rows, valueAccessor, colorScale]);

  const getCellBg = (value) => {
    if (colorScale === 'emerald') return emeraldBg(value, scaleRef);
    if (colorScale === 'amber') return amberBg(value, scaleRef);
    return blueBg(value, scaleRef);
  };

  // YTD column
  const ytdFmt = ytdFormatter || formatter;

  const ytdValues = useMemo(() => {
    if (!ytdAccessor) return null;
    return rows.map(r => ytdAccessor(r));
  }, [rows, ytdAccessor]);

  const ytdScaleRef = useMemo(() => {
    if (!ytdValues) return 0;
    const vals = ytdValues.filter(v => v != null && v !== 0);
    if (vals.length === 0) return 0;
    if (colorScale === 'amber') {
      return Math.max(...vals.map(Math.abs));
    }
    const positives = vals.filter(v => v > 0);
    positives.sort((a, b) => a - b);
    return positives.length > 0 ? positives[positives.length - 1] : 0;
  }, [ytdValues, colorScale]);

  const getCellBgYtd = (value) => {
    if (colorScale === 'emerald') return emeraldBg(value, ytdScaleRef);
    if (colorScale === 'amber') return amberBg(value, ytdScaleRef);
    return blueBg(value, ytdScaleRef);
  };

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr>
                <th className="text-left py-2 pr-3 font-medium text-muted-foreground sticky left-0 bg-card z-10 min-w-[8rem]">{rowHeader}</th>
                {MONTHS.map(m => (
                  <th key={m} className="text-center py-2 px-1 font-medium text-muted-foreground min-w-[3.5rem]">{m}</th>
                ))}
                {ytdAccessor && (
                  <th className="text-center py-2 pl-3 font-semibold text-foreground min-w-[4rem] border-l-2 border-border">{ytdLabel}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {/* Aggregate Total row */}
              {monthTotalAccessor && (
                <tr className="border-b border-border/50">
                  <td className="py-1.5 pr-3 font-semibold whitespace-nowrap sticky left-0 bg-card z-10">Total</td>
                  {MONTHS.map((_, mi) => {
                    const total = monthTotalAccessor(mi);
                    const fcEnd = forecastMonthEnd != null ? forecastMonthEnd : 11;
                    const isForecast = forecastMonthStart != null && mi >= forecastMonthStart && mi <= fcEnd;
                    return (
                      <td key={mi} className={cn(
                        'text-center py-1.5 px-1 font-mono font-semibold',
                        isForecast ? 'text-muted-foreground italic' : 'text-foreground',
                      )}>
                        {total != null ? (monthTotalFormatter || formatter)(total) : emptyLabel}
                      </td>
                    );
                  })}
                  {ytdAccessor && (
                    <td className="text-center py-1.5 pl-3 font-mono font-semibold text-foreground border-l-2 border-border">
                      {ytdTotalRenderer
                        ? ytdTotalRenderer
                        : ytdTotal != null
                          ? (monthTotalFormatter || ytdFmt)(ytdTotal)
                          : emptyLabel}
                    </td>
                  )}
                </tr>
              )}
              {/* Per-row data */}
              {rows.map((r, rIdx) => {
                const ytdVal = ytdValues?.[rIdx];
                const ytdEmpty = ytdVal == null;
                return (
                  <tr key={r.id} className="border-t border-border/30">
                    <td className="py-1.5 pr-3 font-medium whitespace-nowrap sticky left-0 bg-card z-10" title={r.name}>{getLabel(r)}</td>
                    {MONTHS.map((mLabel, mi) => {
                      const value = valueAccessor(r, mi);
                      const isEmpty = isEmptyCell ? isEmptyCell(r, mi, value) : value == null;
                      return (
                        <td
                          key={mi}
                          className={cn(
                            'text-center py-1.5 px-1 font-mono rounded-sm',
                            isEmpty ? GRAY_BG : getCellBg(value)
                          )}
                          title={`${r.name} - ${mLabel}: ${isEmpty ? 'N/A' : formatter(value)}`}
                        >
                          {isEmpty ? emptyLabel : formatter(value)}
                        </td>
                      );
                    })}
                    {ytdValues != null && (
                      <td
                        className={cn(
                          'text-center py-1.5 pl-3 font-mono font-semibold rounded-sm border-l-2 border-border',
                          ytdEmpty ? GRAY_BG : getCellBgYtd(ytdVal)
                        )}
                        title={`${r.name} - ${ytdLabel}: ${ytdEmpty ? 'N/A' : ytdFmt(ytdVal)}`}
                      >
                        {ytdCellRenderer && !ytdEmpty
                          ? ytdCellRenderer(ytdVal, r)
                          : ytdEmpty ? emptyLabel : ytdFmt(ytdVal)}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {subtitle && (
          <p className="text-xs text-muted-foreground mt-2">{subtitle}</p>
        )}
      </CardContent>
    </Card>
  );
}
